import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useUserSettings } from '@/lib/user-settings-context';
import { useToast } from '@/hooks/use-toast';
import { usePlayers } from '@/hooks/use-players';
import { aggregateSessionStats, normalizePlayerRow, UNIT_VALUE } from '@/lib/score';
import { appendNote } from '@/lib/utils';

const BUY_IN_UNIT_KEY = 'ssc_last_buy_in_unit';
import { GoogleSheetSyncService } from '@/services/GoogleSheetSyncService';
import { UiStrategy } from "@/ui/UiStrategy";
import type { ImportExtractedData, ImportExtractedPlayer } from "@/ui/view-props";
import { ImportSessionClassicView } from "@/ui/v1/ImportSessionClassicView";
import { ImportSessionModernView } from "@/ui/v2/ImportSessionModernView";
import { ImportSessionNeonView } from "@/ui/v3/ImportSessionNeonView";

const emptyData: ImportExtractedData = {
  sessionDate: new Date().toISOString().split('T')[0],
  players: [
    { name: '', buyinCount: 0, totalBuyinAmount: 0, cashoutAmount: 0, net: 0, variance: 0, note: null },
  ],
  totalPot: 0,
  totalPositive: 0,
  totalNegative: 0,
  variance: 0,
  adjustmentNote: null,
};

/** Map common import keys to a YYYY-MM-DD string for `<input type="date" />`. */
function parseImportSessionDate(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mm = m[1].padStart(2, '0');
    const dd = m[2].padStart(2, '0');
    return `${m[3]}-${mm}-${dd}`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
}

export default function ImportSession() {
  const { user } = useAuth();
  const { settings } = useUserSettings();
  const { data: players } = usePlayers(user?.id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const savedUnit = localStorage.getItem(BUY_IN_UNIT_KEY);
  const [buyInUnit, setBuyInUnit] = useState(savedUnit ? Number(savedUnit) : UNIT_VALUE);
  const handleSetBuyInUnit = (n: number) => {
    setBuyInUnit(n);
    localStorage.setItem(BUY_IN_UNIT_KEY, String(n));
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawDebug, setRawDebug] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [data, setData] = useState<ImportExtractedData | null>(null);
  const [saving, setSaving] = useState(false);
  const [rawText, setRawText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleTextProcess = async () => {
    if (!rawText.trim()) {
      toast({ variant: 'destructive', title: 'Please paste some text to process' });
      return;
    }

    setLoading(true);
    setError(null);
    setRawDebug(null);

    try {
      // If the user pasted structured JSON, normalize locally and bypass the LLM.
      // This avoids "schema translation" issues (e.g. player/entries/results/score).
      try {
        const parsed = JSON.parse(rawText);
        const obj = (parsed && typeof parsed === 'object') ? (parsed as any) : null;
        const playerRows: unknown[] =
          Array.isArray(parsed) ? parsed
          : Array.isArray(obj?.players) ? obj.players
          : Array.isArray(obj?.data) ? obj.data
          : [];

        if (playerRows.length > 0) {
          const players = playerRows.map((p) => normalizePlayerRow(p, buyInUnit));
          const { totalPositive, totalNegative, variance, totalPot } = aggregateSessionStats(players);
          const sessionDate =
            parseImportSessionDate(obj?.sessionDate ?? obj?.session_date ?? obj?.date) ??
            new Date().toISOString().split('T')[0];

          let adjustmentNote = obj?.adjustmentNote || null;
          players.forEach(p => {
            if (p.note) {
              adjustmentNote = appendNote(adjustmentNote || '', `${p.name}: ${p.note}`);
            }
          });

          setData({
            sessionDate,
            players,
            totalPot,
            totalPositive,
            totalNegative,
            variance,
            adjustmentNote,
          });
          setStep(2);
          return;
        }
      } catch {
        // Not JSON, fall through to the edge function.
      }

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'parse-session-text',
        { body: { text: rawText } }
      );

      if (fnError) {
        console.error('Edge function error:', fnError);
        throw new Error(fnError.message || 'Failed to call text parsing service');
      }

      if (result?.error) {
        console.error('AI extraction error:', result.error);
        if (result.raw) {
          setRawDebug(result.raw);
        }
        setError(result.error);
        setData({ ...emptyData });
        return;
      }

      const players = (result.players || []).map((p: any) => normalizePlayerRow(p, buyInUnit));
      const { totalPositive, totalNegative, variance, totalPot } = aggregateSessionStats(players);
      
      let adjustmentNote = result.adjustmentNote || null;
      players.forEach(p => {
        if (p.note) {
          adjustmentNote = appendNote(adjustmentNote || '', `${p.name}: ${p.note}`);
        }
      });

      const extracted: ImportExtractedData = {
        sessionDate:
          parseImportSessionDate(result.sessionDate ?? result.date ?? result.session_date) ??
          new Date().toISOString().split('T')[0],
        players,
        totalPot,
        totalPositive,
        totalNegative,
        variance,
        adjustmentNote,
      };

      setData(extracted);
      setStep(2);
    } catch (err: any) {
      console.error('Text process error:', err);
      setError(err.message || 'Failed to extract data from text');
      setData({ ...emptyData });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ variant: 'destructive', title: 'Please select an image file' });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setLoading(true);
    setError(null);
    setRawDebug(null);

    try {
      const base64 = await fileToBase64(file);
      console.log('Sending image to AI, size:', base64.length, 'mime:', file.type);

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'ocr-scoresheet',
        { body: { imageBase64: base64, mimeType: file.type } }
      );

      if (fnError) {
        console.error('Edge function error:', fnError);
        throw new Error(fnError.message || 'Failed to call extraction service');
      }

      if (result?.error) {
        console.error('AI extraction error:', result.error);
        if (result.raw) {
          setRawDebug(result.raw);
        }
        setError(result.error);
        return;
      }

      // Populate rawText with the stringified JSON result and stay on Step 1
      setRawText(typeof result === 'string' ? result : JSON.stringify(result, null, 2));
      toast({ title: 'Image processed! Review and click "Process Text" to continue.' });
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to extract data from image');
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = () => {
    setData({ ...emptyData });
    setError(null);
    setRawDebug(null);
    setStep(2);
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        if (!base64 || base64.length < 10) {
          reject(new Error('Failed to convert image to base64'));
          return;
        }
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read image file'));
      reader.readAsDataURL(file);
    });

  const addPlayer = () => {
    if (!data) return;
    setData({
      ...data,
      players: [...data.players, { name: '', buyinCount: 0, totalBuyinAmount: 0, cashoutAmount: 0, net: 0, variance: 0, note: null }],
    });
  };

  const removePlayer = (idx: number) => {
    if (!data || data.players.length <= 1) return;
    const players = data.players.filter((_, i) => i !== idx);
    recalcTotals(players);
  };

  const recalcTotals = (players: ImportExtractedPlayer[]) => {
    const { totalPositive, totalNegative, variance, totalPot } = aggregateSessionStats(players);
    setData(prev => prev ? { ...prev, players, totalPositive, totalNegative, variance, totalPot } : prev);
  };

  const updatePlayer = (idx: number, field: keyof ImportExtractedPlayer, value: string) => {
    if (!data) return;
    const players = [...data.players];
    const p = { ...players[idx] };

    if (field === 'name') {
      p.name = value;
    } else {
      const num = value === '' || value === '-' ? 0 : parseFloat(value);
      (p as any)[field] = isNaN(num) ? 0 : num;
    }

    // Recompute derived fields.
    const normalized = normalizePlayerRow(p, buyInUnit);
    p.buyinCount = normalized.buyinCount;
    p.totalBuyinAmount = normalized.totalBuyinAmount;
    p.cashoutAmount = normalized.cashoutAmount;
    p.net = normalized.net;
    p.variance = normalized.variance;
    p.note = normalized.note;

    players[idx] = p;
    recalcTotals(players);
  };

  const hasDuplicateNames = () => {
    const names = data?.players.map(p => p.name.trim().toLowerCase()).filter(Boolean) || [];
    return new Set(names).size !== names.length;
  };

  const getDuplicateNames = () => {
    const names = data?.players.map(p => p.name.trim().toLowerCase()).filter(Boolean) || [];
    const seen = new Set<string>();
    const dupes = new Set<string>();
    names.forEach(n => { if (seen.has(n)) dupes.add(n); seen.add(n); });
    return dupes;
  };

  const saveSession = async () => {
    if (!user || !data) return;
    setSaving(true);

    try {
      let notes = data.adjustmentNote || '';
      if (Math.abs(data.variance) > 0.01) {
        const varianceNote = `Variance of ${data.variance.toFixed(2)} detected during import.`;
        notes = appendNote(notes, varianceNote);
      }

      const { data: session, error: sessErr } = await supabase
        .from('sessions')
        .insert({
          created_by: user.id,
          date: data.sessionDate ? new Date(data.sessionDate).toISOString() : new Date().toISOString(),
          status: 'unsettled',
          buy_in_unit: buyInUnit,
          notes: notes || null,
        })
        .select()
        .single();

      if (sessErr || !session) throw new Error(sessErr?.message || 'Failed to create session');

      for (const p of data.players) {
        if (!p.name.trim()) continue;

        const { data: existing } = await supabase
          .from('players')
          .select('id')
          .eq('created_by', user.id)
          .ilike('name', p.name)
          .maybeSingle();

        let playerId: string;
        if (existing) {
          playerId = existing.id;
        } else {
          const { data: newPlayer, error: pErr } = await supabase
            .from('players')
            .insert({ name: p.name, created_by: user.id })
            .select()
            .single();
          if (pErr || !newPlayer) throw new Error(pErr?.message || 'Failed to create player');
          playerId = newPlayer.id;
        }

        await supabase.from('session_players').insert({
          session_id: session.id,
          player_id: playerId,
          buy_ins: p.buyinCount,
          cash_out: p.cashoutAmount,
          net_amount: p.net,
        });
      }

      // Upload score sheet image if one was selected via photo/library
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop() || 'jpg';
        const filePath = `${user.id}/${session.id}/score.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('scoresheets')
          .upload(filePath, selectedFile, { upsert: true });
        if (!uploadError) {
          await supabase.from('sessions').update({ score_sheet_path: filePath }).eq('id', session.id);
        } else {
          console.error('Score sheet upload failed:', uploadError);
        }
      }

      // Sync to Google Sheets
      await GoogleSheetSyncService.syncSession(session.id);

      toast({ title: 'Session imported successfully!' });
      navigate(`/session/${session.id}`);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const props = {
    navigateHome: () => navigate("/"),
    step,
    setStep,
    loading,
    error,
    rawDebug,
    previewUrl,
    settings,
    buyInUnit,
    setBuyInUnit: handleSetBuyInUnit,
    rawText,
    setRawText,
    data,
    setData,
    saving,
    onTakePhotoClick: () => fileInputRef.current?.click(),
    onLibraryClick: () => libraryInputRef.current?.click(),
    onTextProcess: handleTextProcess,
    onTryAgain: () => {
      setError(null);
      setRawDebug(null);
    },
    onManualEntry: handleManualEntry,
    addPlayer,
    removePlayer,
    updatePlayer,
    hasDuplicateNames,
    getDuplicateNames,
    saveSession,
    allPlayers: players,
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
          e.target.value = "";
        }}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
          e.target.value = "";
        }}
      />

      <UiStrategy
        classic={<ImportSessionClassicView {...props} />}
        modern={<ImportSessionModernView {...props} />}
        neon={<ImportSessionNeonView {...props} />}
      />
    </>
  );
}
